import { Loader } from '@octopus-do/octopus-storybook'
import { Sitemap } from '@octopus-do/octopus-storybook/icons'
import type { ProjectItem } from '@store/reducers/dashboard/types'
import type React from 'react'
import { Link } from 'react-router-dom'

import { ProjectStatusBadge } from '@/components/ProjectStatusMenu'
import { clsJoin } from '@/utils'
import { useProjectLabel, useProjectLink } from '../../hooks'
import { PinMarker } from '../PinMarker'
import { ProjectActionsMenu } from '../ProjectActionsMenu'
import { ReportFavicon } from '../ReportFavicon'
import { ScheduleMarker } from '../ScheduleMarker'
import { SharedMarker } from '../SharedMarker'
import { UpdateDot } from '../UpdateDot'
import css from './styles.module.css'

interface Props {
  project: ProjectItem
}

// A row has no room for a preview: a sitemap is marked with the sitemap icon,
// a report with the crawled site's favicon, as on its tile.
const RowPreview: React.FC<Props> = ({ project }) => (
  <div className={css.preview}>
    {project.type === 'report' ? (
      <ReportFavicon faviconUrl={project.faviconUrl} className={css.favicon} />
    ) : (
      <Sitemap className={css.icon} />
    )}
  </div>
)

// While the project is being opened the loader takes the icon's place.
const LoadingPreview: React.FC = () => (
  <div className={css.preview}>
    <div className={css.loader}>
      <Loader color="gray" />
    </div>
  </div>
)

// A project as a compact row of the list view: the same link, markers and
// menu as the tile, laid out in one line so that more projects fit the screen.
export const ProjectListRow: React.FC<Props> = ({ project }) => {
  const { loading, onClick, to } = useProjectLink(project)
  const { title, updated } = useProjectLabel(project)

  const isReport = project.type === 'report'

  return (
    <Link
      tabIndex={0}
      to={to}
      className={clsJoin(css.row, loading && css.loading)}
      onClick={onClick}
      aria-busy={loading}
    >
      {loading ? <LoadingPreview /> : <RowPreview project={project} />}
      <div className={css.label}>
        <span className={css.title}>{title}</span>
        {isReport && project.hasUpdate && <UpdateDot />}
        <div className={css.markers}>
          <PinMarker pinned={project.pinned} />
          <SharedMarker email={project.sharedBy} />
          {isReport && project.scheduleActive && (
            <ScheduleMarker
              lastCrawlAt={project.lastCrawlAt}
              nextCrawlAt={project.nextCrawlAt}
            />
          )}
        </div>
      </div>
      <div className={css.status}>
        <ProjectStatusBadge status={project.status ?? null} />
      </div>
      <span className={css.date}>{updated}</span>
      {/* The cell stays while the project opens, so the date does not shift */}
      <div className={css.actions}>
        {!loading && <ProjectActionsMenu project={project} />}
      </div>
    </Link>
  )
}
